import { useCallback, useEffect, useState } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  $createParagraphNode,
  $getNodeByKey,
} from 'lexical'
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  ListNode,
} from '@lexical/list'
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
} from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { $wrapNodes } from '@lexical/selection'
import LexicalToolbar from '../LexicalToolbar'
import ImageInsertDialog from '../ImageInsertDialog'
import VideoInsertDialog from '../VideoInsertDialog'
import YouTubeInsertDialog from '../YouTubeInsertDialog'
import { $createImageNode } from '../nodes/ImageNode'
import { $createVideoNode } from '../nodes/VideoNode'
import { $createYouTubeNode } from '../nodes/YouTubeNode'

export default function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext()
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isLink, setIsLink] = useState(false)
  const [blockType, setBlockType] = useState('paragraph')
  const [textColor, setTextColor] = useState('#000000')
  const [alignment, setAlignment] = useState('left')
  const [showImageDialog, setShowImageDialog] = useState(false)
  const [showVideoDialog, setShowVideoDialog] = useState(false)
  const [showYouTubeDialog, setShowYouTubeDialog] = useState(false)

  const updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))

      const node = selection.anchor.getNode()
      const parent = node.getParent()

      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true)
      } else {
        setIsLink(false)
      }

      const anchorNode = selection.anchor.getNode()
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow()

      const elementKey = element.getKey()
      const elementDOM = editor.getElementByKey(elementKey)

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNodeByKey(elementKey)
          const type = parentList
            ? parentList.getListType()
            : element.getListType()
          setBlockType(type === 'number' ? 'number' : 'bullet')
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType()
          setBlockType(type)
        }
      }

      const style = selection.style
      if (style) {
        const colorMatch = style.match(/color:\s*([^;]+)/)
        if (colorMatch) {
          setTextColor(colorMatch[1])
        }
      }
    }
  }, [editor])

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar()
        return false
      },
      1
    )
  }, [editor, updateToolbar])

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateToolbar()
    })
  }, [editor, updateToolbar])

  const formatText = (format) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
  }

  const formatHeading = (headingType) => {
    if (blockType !== headingType) {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          if (headingType === 'paragraph') {
            $setBlocksType(selection, () => $createParagraphNode())
          } else {
            $setBlocksType(selection, () => $createHeadingNode(headingType))
          }
        }
      })
    }
  }

  const formatList = (listType) => {
    if (listType === 'bullet') {
      if (blockType !== 'bullet') {
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
      } else {
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
      }
    } else {
      if (blockType !== 'number') {
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
      } else {
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
      }
    }
  }

  const insertLink = useCallback(() => {
    if (!isLink) {
      const url = prompt('Enter URL:')
      if (url) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, url)
      }
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    }
  }, [editor, isLink])

  const setColor = (color) => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        selection.formatText('color', color)
      }
    })
    setTextColor(color)
  }

  const setAlign = (align) => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes()
        nodes.forEach((node) => {
          const element = node.getTopLevelElementOrThrow()
          element.setFormat(align)
        })
      }
    })
    setAlignment(align)
  }

  const insertImage = (src, alt) => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const imageNode = $createImageNode(src, alt)
        selection.insertNodes([imageNode, $createParagraphNode()])
      }
    })
  }

  const insertVideo = (src) => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const videoNode = $createVideoNode(src)
        selection.insertNodes([videoNode, $createParagraphNode()])
      }
    })
  }

  const insertYouTube = (videoID) => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const youtubeNode = $createYouTubeNode(videoID)
        selection.insertNodes([youtubeNode, $createParagraphNode()])
      }
    })
  }

  return (
    <>
      <LexicalToolbar
        isBold={isBold}
        isItalic={isItalic}
        isUnderline={isUnderline}
        isLink={isLink}
        blockType={blockType}
        textColor={textColor}
        alignment={alignment}
        onFormatText={formatText}
        onFormatHeading={formatHeading}
        onFormatList={formatList}
        onInsertLink={insertLink}
        onSetColor={setColor}
        onSetAlignment={setAlign}
        onInsertImage={() => setShowImageDialog(true)}
        onInsertVideo={() => setShowVideoDialog(true)}
        onInsertYouTube={() => setShowYouTubeDialog(true)}
      />

      {showImageDialog && (
        <ImageInsertDialog
          onInsert={insertImage}
          onClose={() => setShowImageDialog(false)}
        />
      )}

      {showVideoDialog && (
        <VideoInsertDialog
          onInsert={insertVideo}
          onClose={() => setShowVideoDialog(false)}
        />
      )}

      {showYouTubeDialog && (
        <YouTubeInsertDialog
          onInsert={insertYouTube}
          onClose={() => setShowYouTubeDialog(false)}
        />
      )}
    </>
  )
}
